import React, { useEffect, useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Z_INDEX } from '@app/shared/styles/zIndex';

export const OnlineStatusAnnouncer: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNotification, setShowNotification] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowNotification(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleClose = () => {
    setShowNotification(false);
  };

  return (
    <>
      {/* Screen reader only live region */}
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          zIndex: Z_INDEX.toast,
          padding: 0,
          background: 'none',
          color: 'inherit',
          fontSize: '1px',
          border: 'none',
          clipPath: 'inset(100%)',
          visibility: (!isOnline || (isOnline && showNotification)) ? 'visible' : 'hidden',
        }}
      >
        {!isOnline && t('accessibility.offline_status')}
        {isOnline && showNotification && t('accessibility.online_status')}
      </Box>

      {/* Visual notification */}
      <Snackbar
        open={showNotification}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={isOnline ? 'success' : 'warning'}
          sx={{
            width: '100%',
            px: '8px',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-icon': { mx: '5px', p: 0 },
            '& .MuiAlert-message': { flex: 1, p: 0 },
            '& .MuiAlert-action': { ml: 'auto', p: 0 },
          }}
          role="alert"
        >
          {isOnline ? t('accessibility.online_status') : t('accessibility.offline_status')}
        </Alert>
      </Snackbar>
    </>
  );
};

